<template>
  <div class="list-container">
    <div class="list-header">
      <h1>Roles & Permissions Admin</h1>
    </div>

    <div class="tabs">
      <button 
        :class="{ 'active': activeTab === 'roles' }" 
        @click="activeTab = 'roles'"
      >
        Organization Roles
      </button>
      <button 
        :class="{ 'active': activeTab === 'matrix' }" 
        @click="activeTab = 'matrix'"
      >
        Permissions Matrix
      </button>
    </div>

    <!-- Roles Tab -->
    <div v-if="activeTab === 'roles'" class="tab-content">
      <div class="list-header">
        <h2>Organization Roles</h2>
        <router-link to="/admin/permissions/roles/new" class="important-button big">
          <font-awesome-icon icon="plus" /> New Role
        </router-link>
      </div>

      <div v-if="loading">Loading roles...</div>
      <div v-else-if="error">{{ error }}</div>
      <table v-else>
        <thead>
          <tr>
            <th>Name</th>
            <th>Description</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="role in sortedRoles" :key="role.id">
            <td>{{ role.name }}</td>
            <td class="overflow">{{ role.description }}</td>
            <td>
              <span class="actions">
                <router-link
                  :to="`/admin/permissions/roles/${role.id}/edit`"
                  class="action-button edit"
                  title="Edit"
                >
                  <font-awesome-icon icon="edit" />
                </router-link>
                <button
                  @click="confirmDeleteRole(role)"
                  class="action-button delete"
                  title="Delete"
                >
                  <font-awesome-icon icon="trash" />
                </button>
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Permissions Matrix Tab -->
    <div v-if="activeTab === 'matrix'" class="tab-content">
      <div class="list-header">
        <h2>Permissions Matrix</h2>
      </div>

      <div v-if="loading">Loading matrix...</div>
      <div v-else-if="error">{{ error }}</div>
      <div v-else class="matrix-container">
        <table class="matrix-table">
          <thead>
            <tr>
              <th></th> <!-- Empty corner cell -->
              <th v-for="permission in permissions" :key="permission.id" class="matrix-header">
                {{ permission.name }}
                <div class="matrix-subheader">{{ permission.description }}</div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="role in sortedRoles" :key="role.id">
              <td class="matrix-header">
                {{ role.name }}
                <div class="matrix-subheader">{{ role.description }}</div>
              </td>
              <td v-for="permission in permissions" :key="permission.id" class="matrix-cell">
                <input 
                  type="checkbox"
                  :checked="hasPermission(role, permission)"
                  @change="togglePermission(role, permission)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import axios from 'axios';
import { ref, onMounted, computed } from 'vue';
import { FontAwesomeIcon } from '@/scripts/icons';
import { handleApiError, showConfirmDialog } from '@/scripts/shared-utils';
import { sortRoles } from '@/scripts/permissions';

const activeTab = ref('roles');
const roles = ref([]);
const permissions = ref([]);
const loading = ref(true);
const error = ref(null);
const rolePermissions = ref(new Map()); // Map of role.id -> Set of permission.ids

const sortedRoles = computed(() => {
  return sortRoles(roles.value);
});

async function fetchRoles() {
  try {
    const response = await axios.get('/api/roles');
    roles.value = response.data;
  } catch (error) {
    handleApiError(error, 'fetching roles');
  }
}

async function fetchPermissions() {
  try {
    const response = await axios.get('/api/permissions');
    permissions.value = response.data;
  } catch (error) {
    handleApiError(error, 'fetching permissions');
  }
}

async function confirmDeleteRole(role) {
  if (showConfirmDialog(`Are you sure you want to delete the role "${role.name}"?`)) {
    try {
      await axios.delete(`/api/roles/${role.id}`);
      roles.value = roles.value.filter(r => r.id !== role.id);
    } catch (error) {
      handleApiError(error, 'deleting role');
    }
  }
}

async function loadRolePermissions() {
  for (const role of roles.value) {
    try {
      const response = await axios.get(`/api/roles/${role.id}/permissions`);
      const permissionSet = new Set(response.data.map(p => p.id));
      rolePermissions.value.set(role.id, permissionSet);
    } catch (error) {
      handleApiError(error, `loading permissions for role ${role.name}`);
    }
  }
}

function hasPermission(role, permission) {
  const rolePerms = rolePermissions.value.get(role.id);
  return rolePerms ? rolePerms.has(permission.id) : false;
}

async function togglePermission(role, permission) {
  try {
    const rolePerms = rolePermissions.value.get(role.id) || new Set();
    const newPermissionIds = [...rolePerms];
    
    if (rolePerms.has(permission.id)) {
      // Remove permission
      newPermissionIds.splice(newPermissionIds.indexOf(permission.id), 1);
    } else {
      // Add permission
      newPermissionIds.push(permission.id);
    }
    
    await axios.put(`/api/roles/${role.id}/permissions`, {
      permission_ids: newPermissionIds
    });
    
    // Update local state after successful API call
    const updatedPerms = new Set(newPermissionIds);
    rolePermissions.value.set(role.id, updatedPerms);
  } catch (error) {
    handleApiError(error, 'updating role permissions');
  }
}

async function loadData() {
  loading.value = true;
  error.value = null;
  
  try {
    await Promise.all([
      fetchRoles(),
      fetchPermissions()
    ]);
    await loadRolePermissions(); // Load role-permission mappings
    loading.value = false;
  } catch (err) {
    error.value = 'Failed to load data. Please try refreshing the page.';
    loading.value = false;
  }
}

onMounted(() => {
  loadData();
});
</script>

<style scoped>
@import '@/styles/shared-list.css';
@import '@/styles/shared-tabs.css';
@import '@/styles/shared-matrix.css';
</style>
